'use client';

import { useState, useEffect, useMemo } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { FileText } from 'lucide-react';
import { AdaptiveDialog } from '@/shared/ui/AdaptiveDialog';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { cn } from '@/shared/lib/cn';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/shadcn/alert-dialog';
import { AspectsPills } from './AspectsPills';
import { MaterialTypeSelector } from './MaterialTypeSelector';
import {
  type MaterialCardType,
  referenceCardType,
} from '../hooks/use-project-references-panel';
import type {
  ProjectReference,
  ProjectReferenceAspect,
} from '@/types/project-reference';
import {
  hasImageFile,
  isImageReference,
  effectiveAspects,
} from '@/types/project-reference';

const MAX_DESCRIPTION_LENGTH = 300;

// The four card "types" the unified type selector exposes (View · Document ·
// Reference · Output style). Views are angles edited in AngleEditDialog, so this
// dialog only ever shows Document / Reference / Output cards; View appears as a
// switch-to option (re-filed via materials/convert) only when the parent wires
// the convert. Document / Reference / Output are the SAME project_reference row
// in different states — see referenceCardType + handleChangeType in the panel.
const TYPE_ORDER: MaterialCardType[] = [
  'view',
  'document',
  'reference',
  'output',
];

interface ReferenceDetailDialogProps {
  reference: ProjectReference | null;
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    aspects?: ProjectReferenceAspect[];
    description?: string | null;
  }) => void;
  onDelete: (referenceId: string) => void;
  // Type-selector action: switch this item to another card type (Document /
  // Reference / Output style — a single PATCH — or View via materials/convert).
  // The panel's handleChangeType runs the right hop and closes the dialog.
  onChangeType: (target: MaterialCardType) => void;
  // Whether the View option is offered. Only Brief documents (and Reference /
  // Output, which re-file to a Document first) can become a View, and only when
  // the parent wires the convert; omit/false to hide the View pill.
  canConvertToView?: boolean;
  isSaving?: boolean;
  isDeleting?: boolean;
  isMoving?: boolean;
  isConverting?: boolean;
}

export function ReferenceDetailDialog({
  reference,
  open,
  onClose,
  onSave,
  onDelete,
  onChangeType,
  canConvertToView = false,
  isSaving = false,
  isDeleting = false,
  isMoving = false,
  isConverting = false,
}: ReferenceDetailDialogProps) {
  const t = useTranslations('projectReference');
  const tCommon = useTranslations('common');

  // Show the actual picture whenever the underlying file is an image, even for a
  // document (category 'data') like a satellite/cadastral map or a site plan —
  // the generic file icon hides useful content. Falls back to the icon only for
  // non-image documents (PDF/txt/md).
  const isImage = reference
    ? hasImageFile(reference) || isImageReference(reference)
    : false;
  // Brief-section items document the actual house (site plans, measurements,
  // surveys) — not style inspiration. The dialog must speak about "this
  // document" for them, while reference-section items keep the "how to use
  // this reference" framing. Drive copy off the section, not the file type:
  // a brief item can be an image (e.g. a photo of measurements) and still be
  // documentation of the house.
  const isBrief = reference?.attributes.section === 'brief';

  // Current card type for the unified type selector, derived from the stored
  // state: brief → Document, reference+category 'output' → Output style, else
  // Reference. (Views are angles, edited elsewhere — they never reach this
  // dialog.)
  const currentType: MaterialCardType = reference
    ? referenceCardType(reference)
    : 'document';

  // All four types are offered (View · Document · Reference · Output style),
  // View only when the parent wires the convert. The panel runs the right hop
  // for each (single PATCH for Document/Reference/Output, convert for View).
  const typeOptions: MaterialCardType[] = TYPE_ORDER.filter(
    (type) => type !== 'view' || canConvertToView
  );

  // Aspects (Style/Material/Color chips) describe a Reference's look. They are
  // meaningless for Documents, Output styles, and Views — show the row ONLY when
  // the item is currently a Reference.
  const showAspects = isImage && currentType === 'reference';

  const initialDescription = useMemo(() => {
    if (!reference) return '';
    return (
      reference.attributes.description ??
      reference.attributes.metadata?.classification?.usage_recommendations ??
      reference.attributes.metadata?.extraction?.usage_recommendations ??
      ''
    );
  }, [reference]);

  const initialAspects = useMemo(
    () => (reference ? effectiveAspects(reference) : []),
    [reference]
  );

  const [description, setDescription] = useState(initialDescription);
  const [selectedAspects, setSelectedAspects] =
    useState<ProjectReferenceAspect[]>(initialAspects);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    setDescription(initialDescription);
    setSelectedAspects(initialAspects);
    setShowDeleteDialog(false);
  }, [reference?.id, initialDescription, initialAspects]);

  if (!reference || !open) return null;

  // Aspects are an unordered set — compare membership, not order. Only relevant
  // when the Aspects row is shown (i.e. the item is a Reference).
  const aspectsChanged =
    showAspects &&
    (selectedAspects.length !== initialAspects.length ||
      selectedAspects.some((a) => !initialAspects.includes(a)));

  const hasChanges = description !== initialDescription || aspectsChanged;

  function handleSave() {
    const data: {
      aspects?: ProjectReferenceAspect[];
      description?: string | null;
    } = {};

    if (description !== initialDescription) {
      data.description = description || null;
    }

    if (aspectsChanged && selectedAspects.length > 0) {
      data.aspects = selectedAspects;
    }

    onSave(data);
  }

  const isReTyping = isMoving || isConverting;

  function handleDelete() {
    setShowDeleteDialog(false);
    onDelete(reference!.id);
  }

  const fileName =
    reference.attributes.metadata?.original_filename ??
    reference.attributes.original_filename ??
    'Document';

  return (
    <>
      <AdaptiveDialog
        open={open}
        onClose={onClose}
        title={isBrief ? t('detail.documentTitle') : t('detail.title')}
        onSave={handleSave}
        saveEnabled={hasChanges}
        isSaving={isSaving}
        saveLabel={t('detail.save')}
        onDelete={
          reference.attributes.can_delete
            ? () => setShowDeleteDialog(true)
            : undefined
        }
        isDeleting={isDeleting}
      >
        <div className="space-y-4">
          {isImage && reference.attributes.file_url && (
            <div className="bg-muted rounded-lg overflow-hidden flex items-center justify-center">
              <div className="relative w-full" style={{ maxHeight: 300 }}>
                <Image
                  src={reference.attributes.file_url}
                  alt={fileName}
                  width={600}
                  height={300}
                  className="w-full h-auto object-contain"
                  style={{ maxHeight: 300 }}
                />
              </div>
            </div>
          )}

          {!isImage && (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-muted">
              <FileText className="w-8 h-8 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-list-item-value truncate">
                  {reference.attributes.metadata?.extraction?.document_title ??
                    fileName}
                </p>
                {reference.attributes.metadata?.extraction?.document_title && (
                  <p className="text-list-item-meta text-muted-foreground truncate">
                    {fileName}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Unified type selector — ONE control (View · Document · Reference ·
              Output style). Comes FIRST (right under the image): switching what the
              item is used for is the primary action, and the chosen type decides
              whether aspects are even shown. The shared selector runs the chosen
              hop (single PATCH for Document/Reference/Output, materials/convert
              for View). The server validates and returns a verbatim 422 (e.g.
              view-with-renders) — surfaced by the parent; no gating here. */}
          {reference.attributes.can_delete && (
            <MaterialTypeSelector
              currentType={currentType}
              options={typeOptions}
              busy={isReTyping}
              disabled={isReTyping || isSaving || isDeleting}
              onSelect={onChangeType}
            />
          )}

          {/* Aspects (Style/Material/Color) — shown ONLY for a Reference, since
              they describe the look it contributes; meaningless for Documents,
              Output styles, and Views. */}
          {showAspects && (
            <div className="space-y-2">
              <label className="text-form-label">
                {t('detail.aspectsLabel')}
              </label>
              <AspectsPills
                selected={selectedAspects}
                onChange={setSelectedAspects}
              />
              <p className="text-card-meta text-muted-foreground">
                {t('detail.aspectsHelper')}
              </p>
            </div>
          )}

          {/* Description — secondary, so it sits LAST (below the type selector
              and aspects). */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-form-label">
                {isBrief
                  ? t('detail.documentDescriptionLabel')
                  : t('detail.descriptionLabel')}
              </label>
              <span
                className={cn(
                  'text-card-meta text-muted-foreground',
                  description.length >= MAX_DESCRIPTION_LENGTH &&
                    'text-destructive'
                )}
              >
                {description.length}/{MAX_DESCRIPTION_LENGTH}
              </span>
            </div>
            <Textarea
              value={description}
              onChange={(e) => {
                if (e.target.value.length <= MAX_DESCRIPTION_LENGTH) {
                  setDescription(e.target.value);
                }
              }}
              placeholder={
                isBrief
                  ? t('detail.documentDescriptionPlaceholder')
                  : t('detail.descriptionPlaceholder')
              }
              rows={3}
            />
            <p className="text-card-meta text-muted-foreground">
              {isBrief
                ? t('detail.documentDescriptionHelper')
                : t('detail.descriptionHelper')}
            </p>
          </div>
        </div>
      </AdaptiveDialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('detail.deleteMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {tCommon('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
